'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  DetectionsHoverCardPopover,
  EntityIcon,
  MetricItem,
  type DetectionView,
} from '@terra/ui-design-system';
import { useAssetTableEndpointDetections } from '../../../hooks/api/useAssetTableRelations';
import { getFindingTabInput, getSignalTabInput } from './asset-tab-inputs';
import { useAssetsTableContext } from './AssetsTableContext';
import { rowClickBlockerProps } from '../rowClickBlocker';

type DetectionType = 'finding' | 'signal';

type DetectionMetric = { type: DetectionType; count: number; label: string };

const DETECTION_METRIC_MIN_WIDTH = '48px';

function DetectionMetricValue({ metric }: { metric: DetectionMetric }) {
  const isEmpty = metric.count === 0;

  return (
    <MetricItem
      hideTooltip
      disabled={isEmpty}
      icon={
        <EntityIcon
          type={metric.type}
          appearance={isEmpty ? 'muted' : 'highlighted'}
          density="compact"
        />
      }
      label={metric.label}
      metrics={metric.count}
    />
  );
}

export function AssetDetectionsCell({
  endpointId,
  detectionView,
  findingsCount,
  signalsCount,
}: {
  endpointId: string;
  detectionView: DetectionView;
  findingsCount: number;
  signalsCount: number;
}) {
  const t = useTranslations('proxy.assets.table');
  const { onOpenTab } = useAssetsTableContext();
  const [openType, setOpenType] = useState<DetectionType | null>(null);
  const { findings, signals, isLoading, isError } =
    useAssetTableEndpointDetections({
      endpointId,
      enabled: openType !== null,
    });
  const handleViewFinding = (findingId: string) => {
    const finding = findings.find((item) => item.id === findingId);

    onOpenTab(
      getFindingTabInput({
        id: findingId,
        title: finding?.title ?? t('finding'),
      }),
    );
  };

  const handleViewSignal = (signalId: string) => {
    const signal = signals.find((item) => item.id === signalId);

    onOpenTab(
      getSignalTabInput({ id: signalId, title: signal?.title ?? t('signal') }),
    );
  };

  const metrics: DetectionMetric[] = [
    {
      type: 'signal',
      count: signalsCount,
      label: t('signalsCount', { count: signalsCount }),
    },
    {
      type: 'finding',
      count: findingsCount,
      label: t('findingsCount', { count: findingsCount }),
    },
  ];

  return (
    <HStack
      gap={2.5}
      alignItems="center"
      h="full"
      fontVariantNumeric="tabular-nums"
    >
      {metrics.map((metric) => (
        <Box
          key={metric.type}
          display="inline-flex"
          minW={DETECTION_METRIC_MIN_WIDTH}
        >
          {metric.count === 0 ? (
            <DetectionMetricValue metric={metric} />
          ) : (
            <DetectionsHoverCardPopover
              type={metric.type}
              detectionView={detectionView}
              findings={findings}
              signals={signals}
              isLoading={isLoading}
              isError={isError}
              isOpen={openType === metric.type}
              onOpenChange={(open) => setOpenType(open ? metric.type : null)}
              onViewFinding={handleViewFinding}
              onViewSignal={handleViewSignal}
            >
              <Box as="span" display="inline-flex" {...rowClickBlockerProps}>
                <DetectionMetricValue metric={metric} />
              </Box>
            </DetectionsHoverCardPopover>
          )}
        </Box>
      ))}
    </HStack>
  );
}
